import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Spinner } from '@/components/ui/spinner';
import RequireDepartment from './RequireDepartment';

const AuthenticatedLayout = lazy(() => import('@/layouts/AuthenticatedLayout/AuthenticatedLayout'));
const AuthLayout = lazy(() => import('@/layouts/AuthLayout/AuthLayout'));
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const SetPasswordPage = lazy(() => import('@/features/auth/pages/SetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const RawLeadsPage = lazy(() => import('@/features/leads/pages/RawLeadsPage'));
const LeadDetailPage = lazy(() => import('@/features/leads/pages/LeadDetailPage'));
const CreditRequirementsPage = lazy(() => import('@/features/credit/pages/CreditRequirementsPage'));
const CreditRequirementWizard = lazy(() => import('@/features/credit/pages/CreditRequirementWizard'));
const CreditRequirementDetailPage = lazy(() => import('@/features/credit/pages/CreditRequirementDetailPage'));
const CreditLeadsPage = lazy(() => import('@/features/credit/pages/CreditLeadsPage'));
const CreditApplicationsPage = lazy(() => import('@/features/credit/pages/CreditApplicationsPage'));
const CreditDisbursementsPage = lazy(() => import('@/features/credit/pages/CreditDisbursementsPage'));
const CreditProposalsPage = lazy(() => import('@/features/credit-proposals/pages/CreditProposalsPage'));
const CreditProposalDetailPage = lazy(() => import('@/features/credit-proposals/pages/CreditProposalDetailPage'));
const LendersPage = lazy(() => import('@/features/lenders/pages/LendersPage'));
const LenderDetailPage = lazy(() => import('@/features/lenders/pages/LenderDetailPage'));
const DSAPage = lazy(() => import('@/features/dsa/pages/DSAPage'));
const DSADetailPage = lazy(() => import('@/features/dsa/pages/DSADetailPage'));
const BusinessListPage = lazy(() => import('@/features/business/pages/BusinessListPage'));
const BusinessDetailPage = lazy(() => import('@/features/business/pages/BusinessDetailPage'));
const TechPartnersPage = lazy(() => import('@/features/tech-partners/pages/TechPartnersPage'));
const TechPartnerDetailPage = lazy(() => import('@/features/tech-partners/pages/TechPartnerDetailPage'));
// Loan Origination (LOS) feature has no backend (no matching controller exists anywhere
// in the API) - routes intentionally removed rather than left pointing at dead endpoints.
const InvoicesPage = lazy(() => import('@/features/invoices/pages/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/features/invoices/pages/InvoiceDetailPage'));
const SubscriptionsPage = lazy(() => import('@/features/subscriptions/pages/SubscriptionsPage'));
const EnquiriesPage = lazy(() => import('@/features/enquiries/pages/EnquiriesPage'));
const SubscriptionDetailPage = lazy(() => import('@/features/subscriptions/pages/SubscriptionDetailPage'));
const ProfilePage = lazy(() => import('@/features/settings/pages/ProfilePage'));
const UsersPage = lazy(() => import('@/features/settings/pages/UsersPage'));
const UserDetailPage = lazy(() => import('@/features/settings/pages/UserDetailPage'));
// UserDetailPage route removed: BrokerLenderUserController has no GET-by-id mapping at all,
// so there's no way to fetch a single user - rows in UsersPage are no longer clickable.
const RolesPage = lazy(() => import('@/features/settings/pages/RolesPage'));
const IntegrationsPage = lazy(() => import('@/features/settings/pages/IntegrationsPage'));
const ApprovedProjectsPage = lazy(() => import('@/features/approved-projects/pages/ApprovedProjectsPage'));
const ConsentRegisterPage = lazy(() => import('@/features/consent/pages/ConsentRegisterPage'));
const ApprovalsPage = lazy(() => import('@/features/approvals/pages/ApprovalsPage'));
const RuleSetsPage = lazy(() => import('@/features/bre/pages/RuleSetsPage'));
const CommissionGridsPage = lazy(() => import('@/features/commission/pages/CommissionGridsPage'));
const PayoutReconciliationPage = lazy(() => import('@/features/commission/pages/PayoutReconciliationPage'));
const ClawbackPage = lazy(() => import('@/features/commission/pages/ClawbackPage'));
const AuditLogPage = lazy(() => import('@/features/audit/pages/AuditLogPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/pages/NotificationsPage'));
const NotFoundPage = lazy(() => import('@/features/error-pages/pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('@/features/error-pages/pages/UnauthorizedPage'));
const ErrorPage = lazy(() => import('@/features/error-pages/pages/ErrorPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <Spinner size="lg" />
    </div>
  );
}

function SuspenseWrapper() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter(
  [
    {
      element: <SuspenseWrapper />,
      children: [
        {
          element: <AuthLayout />,
          children: [
            { path: '/login', element: <LoginPage /> },
            { path: '/set-password', element: <SetPasswordPage /> },
            { path: '/forgot-password', element: <ForgotPasswordPage /> },
          ],
        },
        {
          element: <AuthenticatedLayout />,
          children: [
            { index: true, element: <Navigate to="/dashboard" replace /> },
            { path: '/dashboard', element: <DashboardPage /> },
            { path: '/profile', element: <ProfilePage /> },
            { path: '/unauthorized', element: <UnauthorizedPage /> },
            { path: '/error', element: <ErrorPage /> },
            // Any BROKER staff may view the maker-checker queue; only MANAGEMENT can decide.
            { path: '/approvals', element: <ApprovalsPage /> },
            {
              element: <RequireDepartment allowed={['MANAGEMENT', 'SALES', 'HELPDESK']} />,
              children: [
                { path: '/leads', element: <RawLeadsPage /> },
                { path: '/leads/:id', element: <LeadDetailPage /> },
                { path: '/credit-leads', element: <CreditLeadsPage /> },
                { path: '/credit-leads/:id', element: <LeadDetailPage /> },
              ],
            },
            {
              element: <RequireDepartment allowed={['MANAGEMENT', 'RM']} />,
              children: [
                { path: '/credit-requirements', element: <CreditRequirementsPage /> },
                { path: '/credit-applications', element: <CreditApplicationsPage /> },
                { path: '/credit-proposals', element: <CreditProposalsPage /> },
                { path: '/credit-proposals/:id', element: <CreditProposalDetailPage /> },
                { path: '/credit-disbursements', element: <CreditDisbursementsPage /> },
                { path: '/credit-requirements/new', element: <CreditRequirementWizard /> },
                { path: '/credit-requirements/:id', element: <CreditRequirementDetailPage /> },
                { path: '/credit-requirements/:id/edit', element: <CreditRequirementWizard /> },
                { path: '/lenders', element: <LendersPage /> },
                { path: '/lenders/:id', element: <LenderDetailPage /> },
                { path: '/dsa', element: <DSAPage /> },
                { path: '/dsa/:id', element: <DSADetailPage /> },
                { path: '/tech-partners', element: <TechPartnersPage /> },
                { path: '/tech-partners/:id', element: <TechPartnerDetailPage /> },
                { path: '/business', element: <BusinessListPage /> },
                { path: '/business/:id', element: <BusinessDetailPage /> },
                { path: '/eligibility-rule-sets', element: <RuleSetsPage /> },
              ],
            },
            {
              element: <RequireDepartment allowed={['MANAGEMENT', 'SALES']} />,
              children: [{ path: '/enquiries', element: <EnquiriesPage /> }],
            },
            {
              element: <RequireDepartment allowed={['MANAGEMENT', 'FINANCE']} />,
              children: [
                { path: '/invoices', element: <InvoicesPage /> },
                { path: '/invoices/:id', element: <InvoiceDetailPage /> },
                { path: '/subscriptions', element: <SubscriptionsPage /> },
                { path: '/subscriptions/:id', element: <SubscriptionDetailPage /> },
                { path: '/commission-grids', element: <CommissionGridsPage /> },
                { path: '/payout-reconciliation', element: <PayoutReconciliationPage /> },
                { path: '/payout-clawback', element: <ClawbackPage /> },
              ],
            },
            {
              element: <RequireDepartment allowed={['MANAGEMENT']} />,
              children: [
                { path: '/settings/users', element: <UsersPage /> },
                { path: '/settings/users/:id', element: <UserDetailPage /> },
                { path: '/settings/roles', element: <RolesPage /> },
                { path: '/settings/integrations', element: <IntegrationsPage /> },
                { path: '/settings/approved-projects', element: <ApprovedProjectsPage /> },
                { path: '/settings/consent-register', element: <ConsentRegisterPage /> },
                { path: '/settings/audit-log', element: <AuditLogPage /> },
                { path: '/settings/notifications', element: <NotificationsPage /> },
              ],
            },
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);
